/** Shared template engine for per-instance entity numbers (customers, projects, …). */

export const ENTITY_NUMBER_FORMAT_MAX_LEN = 64;
export const ENTITY_NUMBER_SEQ_DIGITS_MIN = 1;
export const ENTITY_NUMBER_SEQ_DIGITS_MAX = 8;

const SEQ_TOKEN_RE = /\{SEQ(?::([1-8]))?\}/;
const YEAR_TOKEN_RE = /\{YEAR\}|\{YY\}/;

export type EntityNumberFormatValidation =
  | { ok: true; format: string }
  | { ok: false; error: string };

export type ManualEntityNumberValidation =
  | { ok: true; value: string }
  | { ok: false; error: string };

export type EntityNumberYearMode = 'none' | 'year' | 'yy';

export type EntityNumberFormatParts = {
  prefix: string;
  yearMode: EntityNumberYearMode;
  seqDigits: number;
};

/** Normalize and validate an entity-number template. */
export function normalizeEntityNumberFormat(raw: unknown): EntityNumberFormatValidation {
  if (raw == null) {
    return { ok: false, error: 'Format ist erforderlich.' };
  }
  const format = String(raw).trim();
  if (!format) {
    return { ok: false, error: 'Format darf nicht leer sein.' };
  }
  if (format.length > ENTITY_NUMBER_FORMAT_MAX_LEN) {
    return {
      ok: false,
      error: `Format darf höchstens ${ENTITY_NUMBER_FORMAT_MAX_LEN} Zeichen lang sein.`,
    };
  }
  if (!SEQ_TOKEN_RE.test(format)) {
    return {
      ok: false,
      error: 'Format muss {SEQ} oder {SEQ:N} (N = 1–8) enthalten.',
    };
  }
  // Reject unknown brace tokens that look like placeholders.
  const tokens = format.match(/\{[^}]+\}/g) ?? [];
  for (const t of tokens) {
    if (t === '{YEAR}' || t === '{YY}' || SEQ_TOKEN_RE.test(t)) continue;
    return { ok: false, error: `Unbekannter Platzhalter: ${t}` };
  }
  return { ok: true, format };
}

export function entityNumberFormatUsesYear(format: string): boolean {
  return YEAR_TOKEN_RE.test(format);
}

export function parseSeqDigits(format: string): number {
  const m = format.match(SEQ_TOKEN_RE);
  if (!m) return 5;
  if (m[1]) {
    const n = Number(m[1]);
    if (
      Number.isInteger(n) &&
      n >= ENTITY_NUMBER_SEQ_DIGITS_MIN &&
      n <= ENTITY_NUMBER_SEQ_DIGITS_MAX
    ) {
      return n;
    }
  }
  return 5;
}

/** Build template from simple UI parts (prefix + optional year + sequence). */
export function buildEntityNumberFormat(parts: EntityNumberFormatParts): string {
  const prefix = String(parts.prefix ?? '').trim().replace(/-+$/g, '');
  const digits = Math.min(
    ENTITY_NUMBER_SEQ_DIGITS_MAX,
    Math.max(ENTITY_NUMBER_SEQ_DIGITS_MIN, Math.floor(parts.seqDigits) || 5),
  );
  const seqToken = `{SEQ:${digits}}`;
  const chunks: string[] = [];
  if (prefix) chunks.push(prefix);
  if (parts.yearMode === 'year') chunks.push('{YEAR}');
  else if (parts.yearMode === 'yy') chunks.push('{YY}');
  chunks.push(seqToken);
  return chunks.join('-');
}

/** Best-effort parse of a stored template into simple UI parts. */
export function parseEntityNumberFormatParts(
  format: string,
  fallbackFormat: string,
): EntityNumberFormatParts {
  const trimmed = String(format ?? '').trim() || fallbackFormat;
  const seqDigits = parseSeqDigits(trimmed);
  let yearMode: EntityNumberYearMode = 'none';
  if (trimmed.includes('{YEAR}')) yearMode = 'year';
  else if (trimmed.includes('{YY}')) yearMode = 'yy';

  let prefix = trimmed
    .replace(/\{YEAR\}/g, '')
    .replace(/\{YY\}/g, '')
    .replace(/\{SEQ(?::[1-8])?\}/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .trim();

  if (!prefix) prefix = '';

  return { prefix, yearMode, seqDigits };
}

/** Counter year key: calendar year when template has year tokens, otherwise 0 (global). */
export function counterYearForFormat(format: string, calendarYear: number): number {
  return entityNumberFormatUsesYear(format) ? calendarYear : 0;
}

/** Render a template with a concrete sequence (does not allocate). */
export function renderEntityNumber(
  format: string,
  seq: number,
  calendarYear: number = new Date().getFullYear(),
): string {
  const digits = parseSeqDigits(format);
  const seqToken = String(Math.max(0, Math.floor(seq))).padStart(digits, '0');
  const yy = String(calendarYear % 100).padStart(2, '0');
  return format
    .replaceAll('{YEAR}', String(calendarYear))
    .replaceAll('{YY}', yy)
    .replace(/\{SEQ(?::[1-8])?\}/g, seqToken);
}

/** Normalize a manually entered entity number (free text). */
export function normalizeManualEntityNumber(
  raw: unknown,
  label: string,
): ManualEntityNumberValidation {
  if (raw == null) {
    return { ok: false, error: `${label} ist erforderlich.` };
  }
  const value = String(raw).trim();
  if (!value) {
    return { ok: false, error: `${label} darf nicht leer sein.` };
  }
  if (value.length > ENTITY_NUMBER_FORMAT_MAX_LEN) {
    return { ok: false, error: `${label} darf höchstens ${ENTITY_NUMBER_FORMAT_MAX_LEN} Zeichen lang sein.` };
  }
  return { ok: true, value };
}

export function isUniqueViolation(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  if (error.code === '23505') return true;
  const msg = String(error.message ?? '').toLowerCase();
  return msg.includes('duplicate') || msg.includes('unique');
}
